/**
 * AcceptInvite Component - Customer App
 *
 * Accept invitation page for customers invited to join an organization.
 * Uses proper customer invitation endpoints:
 * - GET /customer/auth/invitation/{token} - Get invitation details
 * - POST /customer/auth/accept-invite - Accept and set password (new customers)
 * - POST /customer/auth/accept-org-invite - Accept org invite (existing customers)
 *
 * Flow:
 * 1. Extract token from URL (/invite?token=xxx or /invite/xxx)
 * 2. Fetch invitation details (org name, inviter, is_existing_customer)
 * 3. New Customer Flow: Show org info + password form -> Accept -> Redirect to login with email pre-filled
 * 4. Existing Customer Flow: Prompt to login first -> Then accept org invite
 */

import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams, useParams, Link } from 'react-router-dom';
import {
  useCustomerInvitationDetails,
  useCustomerAcceptInvitation,
  useCustomerAcceptOrgInvitation,
  InvitationError,
  type CustomerInvitationDetails,
} from '@cfleet/api-client';
import { useAuth } from '@cfleet/auth';
import { useAuthConfig } from '@/hooks/useAuthConfig';
import {
  Command,
  ArrowRight,
  ArrowLeft,
  ShieldCheck,
  AlertCircle,
  CheckCircle2,
  Loader2,
  UserPlus,
  Building2,
  User,
  Lock,
  Eye,
  EyeOff,
  Clock,
} from 'lucide-react';

type InviteStep = 'loading' | 'show-details' | 'success' | 'error' | 'login-required';

const AcceptInvite: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { token: pathToken } = useParams<{ token?: string }>();
  const { isAuthenticated, refreshToken } = useAuth();

  // Extract token from URL (supports both ?token=xxx and /invite/xxx)
  const token = useMemo(() => {
    const queryToken = searchParams.get('token');
    return queryToken || pathToken || null;
  }, [searchParams, pathToken]);

  // Form state
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [step, setStep] = useState<InviteStep>('loading');
  const [orgAcceptAttempted, setOrgAcceptAttempted] = useState(false);

  // Auth config for authenticated API calls
  const authConfig = useAuthConfig();

  // API hooks
  const {
    data: invitation,
    isLoading: isLoadingInvitation,
    error: invitationError,
  } = useCustomerInvitationDetails(token);

  const {
    mutate: acceptInvitation,
    isPending: isAccepting,
  } = useCustomerAcceptInvitation();

  // Hook for accepting org invitations (existing customers)
  const {
    mutate: acceptOrgInvitation,
    isPending: isAcceptingOrg,
  } = useCustomerAcceptOrgInvitation({ authConfig });

  // Handle invitation loading state and errors
  useEffect(() => {
    if (isLoadingInvitation) {
      setStep('loading');
      return;
    }

    if (invitationError) {
      setStep('error');
      if (invitationError instanceof InvitationError) {
        switch (invitationError.status) {
          case 404:
            setError('This invitation link is invalid or has been cancelled.');
            break;
          case 410:
            setError('This invitation has expired. Please contact the organization for a new invitation.');
            break;
          default:
            setError(invitationError.detail || 'Failed to load invitation details.');
        }
      } else {
        setError('Failed to load invitation details. Please try again.');
      }
      return;
    }

    if (invitation) {
      // Check if existing customer needs to login first
      if (invitation.is_existing_customer && !isAuthenticated) {
        setStep('login-required');
      } else {
        setStep('show-details');
      }
    }
  }, [invitation, isLoadingInvitation, invitationError, isAuthenticated]);

  // Handle authenticated user returning with invitation token (existing customer flow)
  // This auto-accepts the org invitation when user logs in and returns to this page
  useEffect(() => {
    // Only proceed if:
    // 1. User is authenticated
    // 2. We have invitation details
    // 3. It's for an existing customer
    // 4. We have a valid token
    // 5. We haven't already attempted the accept
    // 6. We're not already accepting
    if (
      isAuthenticated &&
      invitation &&
      invitation.is_existing_customer &&
      token &&
      !orgAcceptAttempted &&
      !isAcceptingOrg
    ) {
      setOrgAcceptAttempted(true);
      acceptOrgInvitation(
        { token },
        {
          onSuccess: async () => {
            try {
              await refreshToken();
            } catch {
              // Token refresh failed — customer will get updated orgs on next login
            }
            setStep('success');
            setTimeout(() => navigate('/'), 1500);
          },
          onError: (err) => {
            setOrgAcceptAttempted(false);
            if (err instanceof InvitationError) {
              switch (err.status) {
                case 401:
                  setError('Your session has expired. Please log in again to accept this invitation.');
                  handleLoginRedirect();
                  break;
                case 409:
                  navigate('/');
                  break;
                case 410:
                  setError('This invitation has expired.');
                  setStep('error');
                  break;
                default:
                  setError(err.detail || 'Failed to accept invitation');
                  setStep('error');
              }
            } else {
              setError('An unexpected error occurred');
              setStep('error');
            }
          },
        }
      );
    }
  }, [isAuthenticated, invitation, token, orgAcceptAttempted, isAcceptingOrg, acceptOrgInvitation, navigate]);

  // Validate password
  const validatePassword = (): boolean => {
    if (password.length < 8) {
      setError('Password must be at least 8 characters');
      return false;
    }
    if (password.length > 128) {
      setError('Password must be less than 128 characters');
      return false;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return false;
    }
    return true;
  };

  // Handle form submission for new customers
  const handleAcceptInvitation = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!token) {
      setError('Invalid invitation token');
      return;
    }

    if (!validatePassword()) {
      return;
    }

    acceptInvitation(
      { token, password },
      {
        onSuccess: () => {
          setStep('success');

          // Navigate to login after a brief delay to show success
          // Pass email via router state for pre-filling the login form
          setTimeout(() => {
            navigate('/login', {
              replace: true,
              state: {
                inviteAccepted: true,
                email: invitation?.email,
              },
            });
          }, 1500);
        },
        onError: (err) => {
          if (err instanceof InvitationError) {
            switch (err.status) {
              case 409:
                setError('This account already has a password. Please log in instead.');
                setStep('login-required');
                break;
              case 410:
                setError('This invitation has expired. Please contact the organization for a new invitation.');
                setStep('error');
                break;
              case 400:
                setError(err.detail || 'Invalid request. Please check your password.');
                break;
              default:
                setError(err.detail || 'Failed to accept invitation. Please try again.');
            }
          } else {
            setError('An unexpected error occurred. Please try again.');
          }
        },
      }
    );
  };

  // Handle redirect to login for existing customers
  const handleLoginRedirect = () => {
    // Store the token so we can continue after login
    if (token) {
      sessionStorage.setItem('pending_invite_token', token);
    }
    navigate('/login', { state: { returnTo: `/invite?token=${token}` } });
  };

  // Clear error when user starts typing
  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (error) setError(null);
    setPassword(e.target.value);
  };

  const handleConfirmPasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (error) setError(null);
    setConfirmPassword(e.target.value);
  };

  // Format expiration date
  const formatExpirationDate = (expiresAt: string): string => {
    const date = new Date(expiresAt);
    return date.toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  // Render loading state
  if (step === 'loading') {
    return (
      <div className="min-h-screen bg-zinc-50 flex flex-col items-center justify-center p-4 font-sans text-zinc-900">
        <div className="w-full max-w-md bg-white rounded-2xl shadow-xl border border-zinc-100 p-8">
          <div className="flex flex-col items-center gap-4">
            <Loader2 size={48} className="animate-spin text-blue-600" />
            <p className="text-zinc-600">Loading invitation details...</p>
          </div>
        </div>
      </div>
    );
  }

  // Render error state
  if (step === 'error') {
    return (
      <div className="min-h-screen bg-zinc-50 flex flex-col items-center justify-center p-4 font-sans text-zinc-900 relative overflow-hidden">
        <div className="absolute top-0 left-0 w-full h-96 bg-gradient-to-b from-red-50 to-transparent -z-10"></div>

        <div className="w-full max-w-md bg-white rounded-2xl shadow-xl border border-zinc-100 overflow-hidden">
          <div className="p-8 text-center">
            <div className="w-16 h-16 bg-red-100 text-red-600 rounded-full flex items-center justify-center mx-auto mb-6">
              <AlertCircle size={32} />
            </div>
            <h2 className="text-xl font-bold text-zinc-900 mb-2">Invitation Error</h2>
            <p className="text-sm text-zinc-500 mb-6">{error}</p>
            <div className="flex flex-col items-center gap-3">
              {isAuthenticated && token && (
                <button
                  onClick={() => {
                    setError(null);
                    setOrgAcceptAttempted(false);
                    setStep('loading');
                  }}
                  className="inline-flex items-center gap-2 bg-zinc-900 text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-zinc-800 transition-all"
                >
                  Try Again <ArrowRight size={16} />
                </button>
              )}
              <Link
                to="/login"
                className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-700 font-medium"
              >
                <ArrowLeft size={16} /> Go to Login
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Render success state
  if (step === 'success') {
    return (
      <div className="min-h-screen bg-zinc-50 flex flex-col items-center justify-center p-4 font-sans text-zinc-900 relative overflow-hidden">
        <div className="absolute top-0 left-0 w-full h-96 bg-gradient-to-b from-emerald-50 to-transparent -z-10"></div>

        <div className="w-full max-w-md bg-white rounded-2xl shadow-xl border border-zinc-100 overflow-hidden">
          <div className="p-8 text-center">
            <div className="w-16 h-16 bg-emerald-100 text-emerald-600 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle2 size={32} />
            </div>
            <h2 className="text-xl font-bold text-zinc-900 mb-2">Welcome to {invitation?.fleet_company_name}!</h2>
            <p className="text-sm text-zinc-500 mb-2">
              Your account has been set up successfully.
            </p>
            <p className="text-xs text-zinc-400">
              Redirecting to sign in...
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Render login required state (existing customers)
  if (step === 'login-required') {
    return (
      <div className="min-h-screen bg-zinc-50 flex flex-col items-center justify-center p-4 font-sans text-zinc-900 relative overflow-hidden">
        <div className="absolute top-0 left-0 w-full h-96 bg-gradient-to-b from-blue-50 to-transparent -z-10"></div>

        <div className="w-full max-w-md bg-white rounded-2xl shadow-xl border border-zinc-100 overflow-hidden">
          <div className="p-8">
            {/* Logo */}
            <div className="flex justify-center mb-8">
              <div className="flex items-center gap-2.5">
                <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center text-white shadow-lg shadow-blue-500/30">
                  <Command size={20} />
                </div>
                <span className="font-bold text-3xl text-zinc-800 tracking-tight">cfleet</span>
              </div>
            </div>

            <div className="mb-6 text-center">
              <div className="inline-flex items-center justify-center w-12 h-12 bg-blue-50 text-blue-600 rounded-full mb-4">
                <Building2 size={24} />
              </div>
              <h2 className="text-xl font-bold text-zinc-900">Join {invitation?.fleet_company_name}</h2>
              <p className="text-sm text-zinc-500 mt-2">
                You already have an account. Please log in to accept this invitation.
              </p>
            </div>

            {/* Invitation Details */}
            {invitation && (
              <div className="mb-6 p-4 bg-zinc-50 rounded-lg space-y-2">
                <div className="flex items-center gap-2 text-sm">
                  <User size={16} className="text-zinc-400" />
                  <span className="text-zinc-600">Invited by:</span>
                  <span className="font-medium text-zinc-900">{invitation.invited_by_name}</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <Clock size={16} className="text-zinc-400" />
                  <span className="text-zinc-600">Expires:</span>
                  <span className="font-medium text-zinc-900">{formatExpirationDate(invitation.expires_at)}</span>
                </div>
              </div>
            )}

            {error && (
              <div className="mb-6 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
                <AlertCircle size={18} className="text-red-500 shrink-0 mt-0.5" />
                <p className="text-sm font-medium text-red-700">{error}</p>
              </div>
            )}

            <button
              onClick={handleLoginRedirect}
              className="w-full bg-zinc-900 text-white py-3 rounded-lg text-sm font-semibold shadow-lg hover:bg-zinc-800 hover:shadow-xl hover:-translate-y-0.5 transition-all flex items-center justify-center gap-2"
            >
              Log In to Continue <ArrowRight size={18} />
            </button>
          </div>

          <div className="px-8 py-5 bg-zinc-50 border-t border-zinc-100 text-center">
            <p className="text-xs text-zinc-500">
              Don't have an account?{' '}
              <span className="font-medium text-zinc-700">
                Contact the organization administrator
              </span>
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Render invitation details and password form (new customers)
  return (
    <div className="min-h-screen bg-zinc-50 flex flex-col items-center justify-center p-4 font-sans text-zinc-900 relative overflow-hidden">
      {/* Background Decor */}
      <div className="absolute top-0 left-0 w-full h-96 bg-gradient-to-b from-blue-50 to-transparent -z-10"></div>

      <div className="w-full max-w-md bg-white rounded-2xl shadow-xl border border-zinc-100 overflow-hidden">
        <div className="p-8">
          {/* Logo */}
          <div className="flex justify-center mb-8">
            <div className="flex items-center gap-2.5">
              <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center text-white shadow-lg shadow-blue-500/30">
                <Command size={20} />
              </div>
              <span className="font-bold text-3xl text-zinc-800 tracking-tight">cfleet</span>
            </div>
          </div>

          <div className="mb-6 text-center">
            <div className="inline-flex items-center justify-center w-12 h-12 bg-blue-50 text-blue-600 rounded-full mb-4">
              <UserPlus size={24} />
            </div>
            <h2 className="text-xl font-bold text-zinc-900">Set up your account</h2>
            <p className="text-sm text-zinc-500 mt-2">
              You've been invited to join <span className="font-semibold text-zinc-700">{invitation?.fleet_company_name}</span>
            </p>
          </div>

          {/* Invitation Details */}
          {invitation && (
            <div className="mb-6 p-4 bg-zinc-50 rounded-lg space-y-2">
              <div className="flex items-center gap-2 text-sm">
                <User size={16} className="text-zinc-400" />
                <span className="text-zinc-600">Invited by:</span>
                <span className="font-medium text-zinc-900">{invitation.invited_by_name}</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <Building2 size={16} className="text-zinc-400" />
                <span className="text-zinc-600">Email:</span>
                <span className="font-medium text-zinc-900">{invitation.email}</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <Clock size={16} className="text-zinc-400" />
                <span className="text-zinc-600">Expires:</span>
                <span className="font-medium text-zinc-900">{formatExpirationDate(invitation.expires_at)}</span>
              </div>
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div className="mb-6 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
              <AlertCircle size={18} className="text-red-500 shrink-0 mt-0.5" />
              <p className="text-sm font-medium text-red-700">{error}</p>
            </div>
          )}

          {/* Password Form */}
          <form onSubmit={handleAcceptInvitation} className="space-y-5">
            <div>
              <label htmlFor="password" className="block text-xs font-medium text-zinc-700 mb-1.5">
                Create Password
              </label>
              <div className="relative group">
                <div className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400 group-focus-within:text-blue-500 transition-colors">
                  <Lock size={18} />
                </div>
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  required
                  minLength={8}
                  maxLength={128}
                  value={password}
                  onChange={handlePasswordChange}
                  disabled={isAccepting}
                  className="w-full pl-10 pr-10 py-3 bg-zinc-50 border border-zinc-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all placeholder:text-zinc-400 font-medium disabled:opacity-50"
                  placeholder="Minimum 8 characters"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-600 transition-colors"
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            <div>
              <label htmlFor="confirm-password" className="block text-xs font-medium text-zinc-700 mb-1.5">
                Confirm Password
              </label>
              <div className="relative group">
                <div className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400 group-focus-within:text-blue-500 transition-colors">
                  <Lock size={18} />
                </div>
                <input
                  id="confirm-password"
                  type={showConfirmPassword ? 'text' : 'password'}
                  required
                  minLength={8}
                  maxLength={128}
                  value={confirmPassword}
                  onChange={handleConfirmPasswordChange}
                  disabled={isAccepting}
                  className="w-full pl-10 pr-10 py-3 bg-zinc-50 border border-zinc-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all placeholder:text-zinc-400 font-medium disabled:opacity-50"
                  placeholder="Re-enter your password"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-600 transition-colors"
                >
                  {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={isAccepting}
              className="w-full bg-zinc-900 text-white py-3 rounded-lg text-sm font-semibold shadow-lg hover:bg-zinc-800 hover:shadow-xl hover:-translate-y-0.5 transition-all flex items-center justify-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed disabled:transform-none disabled:hover:shadow-lg"
            >
              {isAccepting ? (
                <Loader2 size={20} className="animate-spin" />
              ) : (
                <>
                  Create Account & Join <ArrowRight size={18} />
                </>
              )}
            </button>
          </form>
        </div>

        <div className="px-8 py-5 bg-zinc-50 border-t border-zinc-100 text-center">
          <p className="text-xs text-zinc-500">
            Already have an account?{' '}
            <Link
              to="/login"
              className="font-medium text-blue-600 hover:text-blue-700 hover:underline"
            >
              Sign In
            </Link>
          </p>
        </div>
      </div>

      {/* Footer Info */}
      <div className="mt-8 text-center space-y-3">
        <div className="flex items-center justify-center gap-2 text-xs text-emerald-600 font-medium bg-emerald-50 px-3 py-1 rounded-full w-fit mx-auto border border-emerald-100">
          <ShieldCheck size={12} /> Secure Connection
        </div>
        <p className="text-xs text-zinc-400">© 2025 cfleet. All rights reserved.</p>
      </div>
    </div>
  );
};

export default AcceptInvite;
